import Chart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import { Box, Stack, Typography, useTheme } from "@mui/material";

import { Strings } from "@app/constants";
import { useRequestState } from "@app/hooks";
import { selectDashboardState } from "../selector";

const RevenueChartCard = () => {
  const { data } = useRequestState({
    stateSelector: selectDashboardState,
  });
  const theme = useTheme();
  const breakdown = data?.revenue?.breakdown || [];

  const labels = breakdown.map((item: { category: string }) => item.category);
  const series = breakdown.map((item: { count: number }) => item.count);
  const total = series.reduce((sum: number, val: number) => sum + val, 0);

  const colors = [theme.palette.primary.darker, theme.palette.primary.light, theme.palette.primary.lighter, theme.palette.primary.dark, theme.palette.primary.main];

  const chartOptions: ApexOptions = {
    chart: { type: "donut" },
    colors: colors.slice(0, series.length),
    labels,
    legend: { show: false },
    dataLabels: { enabled: false },
    plotOptions: {
      pie: {
        donut: {
          size: "70%",
          labels: {
            show: true,
            total: {
              show: true,
              label: "Sales",
              fontSize: "16px",
              fontWeight: 600,
              formatter: () => total.toLocaleString(),
            },
          },
        },
      },
    },
    stroke: {
      show: true,
      width: 6,
      colors: ["#fff"],
    },
    tooltip: {
      y: {
        formatter: (val: number) => val.toLocaleString(),
      },
    },
  };

  const fontFamily = Strings.fontLanguage.workSans;

  return (
    <Box
      sx={{
        backgroundColor: "background.default",
        borderRadius: 1,
        p: 3,
        width: "100%",
        boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
        display: "flex",
      }}
    >
      <Stack width={"100%"}>
        <Typography
          fontFamily={fontFamily}
          color="#1A202C"
          fontWeight={600}
          sx={{ marginBottom: 6 }}
        >
          {Strings.dashboard.revenue}
        </Typography>

        <Stack direction={{ xs: "column", sm: "row" }} width="100%">
          {series?.length > 0 && (
            <Box
              sx={{
                width: {
                  xs: "100%",
                  sm: "50%",
                },
              }}
            >
              <Chart
                options={chartOptions}
                series={series}
                type="donut"
                width="100%"
                height={280}
              />
            </Box>
          )}

          <Stack
            spacing={3}
            justifyContent="center"
            alignItems="center"
            sx={{ width: "100%" }}
            ml={{ sm: 0, med: 2 }}
          >
            {labels?.map((label: string, index: number) => (
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                key={label}
                width="100%"
                px={2}
              >
                <Stack direction="row" alignItems="center" gap={1.5}>
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      backgroundColor: colors[index % colors.length],
                    }}
                  />
                  <Typography variant="body2" color="#90A3BF">
                    {label}
                  </Typography>
                </Stack>
                <Typography variant="body2" fontWeight={500}>
                  {series[index].toLocaleString()}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Stack>
      </Stack>
    </Box>
  );
};

export { RevenueChartCard };
